import { useEffect, useMemo, useState } from "react";

import { useNode } from "@craftjs/core";
import { cloneDeep } from "lodash";
import { PlusIcon } from "lucide-react";
import { Button, DefaultPopover, Separator, cn } from "twyne-new.ui";

import { TailwindSandboxTwyne } from "@/modules/cms/views/post/components/builder/Content/TailwindSandbox/Twyne";
import { useUtilities } from "@/modules/cms/views/post/components/builder/context";

import { ButtonSandbox, ListContainer, SearchInput, SeparatorSandbox, Subtitle, TitleSandbox } from "../../properties/components/tailwindSandbox";
import { useToolbar } from "../Toolbar/context";
import { useBlockDefinition } from "../definition";
import { usePureCraftProp } from "../hooks";
import { UTILITIES_ALIASES, UTILITIES_FLAT } from "../predefined_sizes";
import { useCustomUtilities } from "../tailwindConfig/hooks";

import AddPopover from "./AddPopover";
import ContextMenu from "./ContextMenu";
import { useUtilitiesContext, UtilitiesProvider } from "./context";

const UtilitiesWrapper = props => <>
  <UtilitiesProvider>
    <Utilities {...props} />
  </UtilitiesProvider>
</>

const useBlockUtilities = () => {
  const { id, custom } = useNode(({ data: { custom } }) => ({ custom }))
  const { getDefinition } = useBlockDefinition()
  const definition = getDefinition(id)
  return useMemo(() => (definition?.defaultUtilities || custom?.utilities || []).map(utility => [utility]), [definition, custom])
}

const Utilities = ({ trigger }) => {
  const { value: customUtilities } = useCustomUtilities()
  const { utilitiesContext, utilitiesContext: { items, search }, setUtilitiesContext, onCloseContextMenu } = useUtilitiesContext()
  const { selectedUtilities } = useUtilities()
  const { value: props } = usePureCraftProp('properties')
  const blockUtilities = useBlockUtilities()

  const [open, setOpen] = useState(false)

  const { useChangeOpenPopover } = useToolbar()
  useChangeOpenPopover({ open: open || selectedUtilities.length, value: 'utilities' })

  const handleChangeSearch = value => {
    setUtilitiesContext({ ...utilitiesContext, search: value })
  }

  useEffect(() => {
    if (!open && utilitiesContext.search) {
      setUtilitiesContext({ ...utilitiesContext, search: '' })
    }
  }, [open, setUtilitiesContext, utilitiesContext])

  const usedUtilities = useMemo(() => findUseUtilities(props, [...UTILITIES_FLAT, ...customUtilities]), [props, customUtilities])

  return <>
    <DefaultPopover
      trigger={trigger}
      triggerProps={{ asChild: true }}
      contentProps={{ className: 'w-[600px]', side: 'left', align: 'center' }}
      wrapperProps={{ open: open, onOpenChange: setOpen }}
    >
      <div className="relative">
        <SearchInput title='Utilities' value={search} onChange={handleChangeSearch} onClose={() => setOpen(false)} />
        <Separator className='m-0 my-2' />
        <ContextMenu />
        <AddUtilityButton  />
        <div className={cn(blockUtilities.length ? 'h-[750px]' : 'h-[650px]')} onClick={() => onCloseContextMenu()}>
          <TailwindSandboxTwyne>
            <TitleSandbox>Predefined</TitleSandbox>
            {!items.length ? <div className='flex w-full justify-center h-92 mt-4'>No utility found</div> : <List items={items} className='h-96' />}

            <SeparatorSandbox />

            <TitleSandbox>Custom</TitleSandbox>
            <List items={[{ category: '', items: customUtilities }]} custom className='h-18' />

            <SeparatorSandbox />

            <TitleSandbox>Used Utilities</TitleSandbox>
            <List items={[{ category: '', items: usedUtilities }]} custom used className='h-18' />

            {blockUtilities.length ? <>
              <SeparatorSandbox />
              <TitleSandbox>Block Utilities</TitleSandbox>
              <List items={[{ category: '', items: blockUtilities }]} custom used className='h-18' />
            </> : null}
          </TailwindSandboxTwyne>
        </div>
      </div>
    </DefaultPopover>
  </>
}

function AddUtilityButton() {
  const { value: customUtilities, setValue: setCustomUtilities } = useCustomUtilities()

  const handleAdd = newUtility => {
    const utilitiesCopy = cloneDeep(customUtilities)
    setCustomUtilities([...utilitiesCopy, newUtility])
  }

  return <AddPopover
    trigger={<Button className='w-fit absolute top-115 left-12 py-px' variant='light' size='sm'> <PlusIcon size='14' /> </Button>}
    onSubmit={handleAdd}
  />
}

const List = ({ items = [], custom, used, className = '' }) => {
  const { onCloseContextMenu } = useUtilitiesContext()

  return <>
    <ListContainer className={`${className} p-1`} onCloseContextMenu={onCloseContextMenu}>
      {items.map(({ category, items }) => <div key={category}>
        <Subtitle subtitle={category} />
        <div className="flex gap-2 flex-wrap mb-2">
          {items.map((item, index) => <Item key={index} value={item?.value || item} label={item?.label || item} custom={custom} used={used} />)}
        </div>
      </div>)}
    </ListContainer>
  </>
}

const Item = ({ value, label, custom, used }) => {
  const { selectedUtilities = [], setSelectedUtilities } = useUtilities()
  const { setUtilitiesContext, utilitiesContext } = useUtilitiesContext()

  const isComposedSelected = useMemo(() => used && JSON.stringify(selectedUtilities) === JSON.stringify(value), [selectedUtilities, value, used])

  const handleRightClick = (value, event) => {
    event.preventDefault()
    setUtilitiesContext({ ...utilitiesContext, rightClick: { selected: value, mouse: { x: event.clientX, y: event.clientY }, custom } })
  }

  const handleClick = () => {
    if (used) {
      return setSelectedUtilities(isComposedSelected ? [] : value)
    }

    const alreadySelected = selectedUtilities.includes(value)
    setSelectedUtilities(alreadySelected ? selectedUtilities.filter(u => u !== value) : [...selectedUtilities, value])
  }

  return <ButtonSandbox
    active={isComposedSelected || selectedUtilities.includes(value)}
    onClick={handleClick}
    onContextMenu={e => handleRightClick(value, e)}
    size='sm'
    variant='outline'
    color='accent'
  >
    {used ? value.map(value => UTILITIES_ALIASES[value] || value).join(' > ') : label}
  </ButtonSandbox>
}

function findUseUtilities(props, utilities) {
  const utilitiesSet = new Set(utilities)
  const utilitiesFound = []

  function collect(obj, path = []) {
    if (!obj || typeof obj !== "object") return

    for (const key of Object.keys(obj)) {
      const nextPath = [...path, key]
      const value = obj[key]
      if (value === null || value === undefined) continue

      collect(value, nextPath)

      const parts = nextPath.filter(k => utilitiesSet.has(k))
      if (parts.length > 0 && typeof value !== "object") {
        utilitiesFound.push(parts)
      }
    }
  }

  collect(props)
  return [...new Set(utilitiesFound.map(JSON.stringify))].map(JSON.parse)
}

export default UtilitiesWrapper
